/**
 * Side cart drawer — open on add-to-cart / header cart click.
 */
(function () {
  'use strict';

  var FREE_SHIPPING_THRESHOLD = 9900;
  var root;
  var overlay;
  var drawer;
  var itemsEl;
  var emptyEl;
  var footerEl;
  var shippingEl;
  var subtotalEl;
  var moneyFormat;
  var isOpen = false;
  var isUpdating = false;
  var lastSignature = null;
  var refreshTimer = null;
  var settleTimers = [];

  // Captured before patching so our own requests never re-trigger the watcher.
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  // Apps such as BOGOS add their free gift line after our add-to-cart resolves,
  // so re-check the cart for a short window after every mutation.
  var SETTLE_DELAYS = [700, 1600, 3000];

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, moneyFormat || theme.moneyFormat);
    }

    var amount = (Number(cents) / 100).toFixed(2);
    var format = moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function imageUrl(item) {
    if (!item.image) return '';
    // Insert Shopify size filter before file extension when missing
    if (/_\d+x\d*\./.test(item.image)) return item.image;
    return item.image.replace(/(\.\w+)(\?.*)?$/, '_160x$1$2');
  }

  function shippingMessage(total) {
    var threshold = FREE_SHIPPING_THRESHOLD;
    if (total >= threshold) {
      return '<div class="free-shipping-banner free-shipping-banner--achieved">✓ You\'ve unlocked <strong>free shipping</strong>!</div>';
    }
    if (total === 0) {
      return '<div class="free-shipping-banner">Spend <strong>' + formatMoney(threshold) + '</strong> or more to unlock free shipping.</div>';
    }
    return '<div class="free-shipping-banner">Spend <strong>' + formatMoney(threshold - total) + '</strong> more to unlock <strong>free shipping</strong>.</div>';
  }

  // Gift lines are owned by the free gift app, so they render read-only.
  function isGiftItem(item) {
    var props = item.properties || {};
    for (var key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key) && /gift|bogos/i.test(key)) {
        return true;
      }
    }
    return item.final_line_price === 0 || item.line_price === 0;
  }

  function renderItem(item) {
    var variantHtml = '';
    if (item.variant_title && item.variant_title.indexOf('Default') === -1) {
      variantHtml = '<p class="side-cart__item-variant">' + escapeHtml(item.variant_title) + '</p>';
    }

    var img = item.image
      ? '<img src="' + escapeHtml(imageUrl(item)) + '" alt="' + escapeHtml(item.product_title) + '" width="80" height="80" loading="lazy">'
      : '';

    var gift = isGiftItem(item);
    var controlsHtml;
    var actionHtml;
    var priceHtml;

    if (gift) {
      controlsHtml = '<span class="side-cart__item-qty-static">Qty ' + item.quantity + '</span>';
      actionHtml = '';
      priceHtml = '<span class="side-cart__item-price side-cart__item-price--free">Free</span>';
    } else {
      controlsHtml =
        '<div class="side-cart__qty" data-key="' + escapeHtml(item.key) + '">' +
          '<button type="button" class="side-cart__qty-btn" data-qty-change="-1" aria-label="Decrease quantity">−</button>' +
          '<input type="number" class="side-cart__qty-input" value="' + item.quantity + '" min="0" data-qty-input aria-label="Quantity">' +
          '<button type="button" class="side-cart__qty-btn" data-qty-change="1" aria-label="Increase quantity">+</button>' +
        '</div>';
      actionHtml = '<button type="button" class="side-cart__remove" data-remove data-key="' + escapeHtml(item.key) + '">Remove</button>';
      priceHtml = '<span class="side-cart__item-price">' + formatMoney(item.line_price) + '</span>';
    }

    return (
      '<li class="side-cart__item' + (gift ? ' side-cart__item--gift' : '') + '" data-key="' + escapeHtml(item.key) + '">' +
        '<a href="' + escapeHtml(item.url) + '" class="side-cart__item-image">' + img + '</a>' +
        '<div class="side-cart__item-details">' +
          (gift ? '<span class="side-cart__gift-badge">Free gift</span>' : '') +
          '<a href="' + escapeHtml(item.url) + '" class="side-cart__item-title">' + escapeHtml(item.product_title) + '</a>' +
          variantHtml +
          '<div class="side-cart__item-row">' +
            controlsHtml +
            priceHtml +
          '</div>' +
          actionHtml +
        '</div>' +
      '</li>'
    );
  }

  function updateHeaderCount(count) {
    var $count = window.jQuery ? jQuery('span.item-count') : null;
    if ($count && $count.length) {
      $count.text(count);
    }
    var cartCount = document.getElementById('CartCount');
    if (cartCount) {
      cartCount.classList.toggle('hidden-count', count < 1);
    }
  }

  function cartSignature(cart) {
    var items = (cart.items || [])
      .map(function (item) {
        return item.key + ':' + item.quantity + ':' + item.line_price;
      })
      .join('|');
    return cart.item_count + '/' + cart.total_price + '/' + items;
  }

  function render(cart) {
    if (!root) return;

    lastSignature = cartSignature(cart);
    shippingEl.innerHTML = shippingMessage(cart.total_price);
    subtotalEl.textContent = formatMoney(cart.total_price);
    updateHeaderCount(cart.item_count);

    if (!cart.items || cart.items.length === 0) {
      itemsEl.innerHTML = '';
      emptyEl.hidden = false;
      footerEl.hidden = true;
      return;
    }

    emptyEl.hidden = true;
    footerEl.hidden = false;
    itemsEl.innerHTML = cart.items.map(renderItem).join('');
  }

  function request(url, options) {
    var opts = options || {};
    opts.credentials = 'same-origin';
    // Use the live fetch so apps that wrap it still observe our cart writes.
    var doFetch = window.fetch || nativeFetch;
    return doFetch.call(window, url, opts).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed: ' + url);
      return res.json();
    });
  }

  function fetchCart() {
    return request('/cart.js', { headers: { Accept: 'application/json' } });
  }

  // Re-render only when the cart actually differs, so background polling
  // caused by the free gift app does not make the drawer flicker.
  function refreshIfChanged() {
    return fetchCart()
      .then(function (cart) {
        if (cartSignature(cart) !== lastSignature) render(cart);
        return cart;
      })
      .catch(function () {});
  }

  function clearSettleTimers() {
    settleTimers.forEach(clearTimeout);
    settleTimers = [];
  }

  // Free gift apps mutate the cart asynchronously after our own add resolves.
  function scheduleSettleChecks() {
    clearSettleTimers();
    settleTimers = SETTLE_DELAYS.map(function (delay) {
      return setTimeout(refreshIfChanged, delay);
    });
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshIfChanged();
      scheduleSettleChecks();
    }, 250);
  }

  // Watch cart mutations made by other scripts (free gift app, other apps).
  function watchExternalCartChanges() {
    if (nativeFetch) {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = nativeFetch(input, init);
        if (CART_MUTATION_RE.test(url)) {
          promise.then(scheduleRefresh, function () {});
        }
        return promise;
      };
    }

    var XHR = window.XMLHttpRequest;
    if (!XHR || !XHR.prototype) return;

    var nativeOpen = XHR.prototype.open;
    XHR.prototype.open = function (method, url) {
      this.__sideCartUrl = url;
      return nativeOpen.apply(this, arguments);
    };

    var nativeSend = XHR.prototype.send;
    XHR.prototype.send = function () {
      var xhr = this;
      if (CART_MUTATION_RE.test(xhr.__sideCartUrl || '')) {
        xhr.addEventListener('load', scheduleRefresh);
      }
      return nativeSend.apply(this, arguments);
    };
  }

  function changeLine(key, quantity) {
    if (isUpdating) return Promise.resolve();
    isUpdating = true;
    root.classList.add('is-loading');

    return request('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (cart) {
        render(cart);
        // Gift eligibility can change when quantities change.
        scheduleSettleChecks();
        return cart;
      })
      .catch(function () {
        return refreshIfChanged();
      })
      .finally(function () {
        isUpdating = false;
        root.classList.remove('is-loading');
      });
  }

  function open() {
    if (!root || isOpen) return;
    isOpen = true;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('side-cart-open');
    document.body.classList.add('side-cart-open');
    if (drawer) drawer.focus();
  }

  function close() {
    if (!root || !isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('side-cart-open');
    document.body.classList.remove('side-cart-open');
  }

  function refresh(cart) {
    if (cart) {
      render(cart);
      scheduleSettleChecks();
      return Promise.resolve(cart);
    }
    return fetchCart().then(function (freshCart) {
      render(freshCart);
      return freshCart;
    });
  }

  function openAndRefresh() {
    return refresh().then(function () {
      open();
    });
  }

  function onQtyClick(e) {
    var btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    var wrap = btn.closest('.side-cart__qty');
    if (!wrap) return;
    var input = wrap.querySelector('[data-qty-input]');
    var key = wrap.getAttribute('data-key');
    var delta = parseInt(btn.getAttribute('data-qty-change'), 10);
    var next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
    input.value = next;
    changeLine(key, next);
  }

  function onQtyInput(e) {
    var input = e.target.closest('[data-qty-input]');
    if (!input) return;
    var wrap = input.closest('.side-cart__qty');
    if (!wrap) return;
    var key = wrap.getAttribute('data-key');
    var next = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(key, next);
  }

  function onRemove(e) {
    var btn = e.target.closest('[data-remove]');
    if (!btn) return;
    changeLine(btn.getAttribute('data-key'), 0);
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      close();
    }
  }

  function bindHeaderCart() {
    var links = document.querySelectorAll('a.site-header__cart');
    Array.prototype.forEach.call(links, function (link) {
      link.classList.add('js-side-cart-open');
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openAndRefresh();
      });
    });
  }

  function init() {
    root = document.getElementById('SideCart');
    if (!root) return;

    overlay = root.querySelector('.side-cart__overlay');
    drawer = root.querySelector('.side-cart__drawer');
    itemsEl = root.querySelector('[data-side-cart-items]');
    emptyEl = root.querySelector('[data-side-cart-empty]');
    footerEl = root.querySelector('[data-side-cart-footer]');
    shippingEl = root.querySelector('[data-side-cart-shipping]');
    subtotalEl = root.querySelector('[data-side-cart-subtotal]');
    moneyFormat = root.getAttribute('data-money-format') || (window.theme && theme.moneyFormat);
    FREE_SHIPPING_THRESHOLD = parseInt(root.getAttribute('data-free-shipping-threshold'), 10) || 9900;

    fetchCart()
      .then(render)
      .catch(function () {
        if (shippingEl) shippingEl.innerHTML = shippingMessage(0);
      });

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-side-cart-close]')) {
        close();
        return;
      }
      onQtyClick(e);
      onRemove(e);
    });

    root.addEventListener('change', onQtyInput);
    document.addEventListener('keydown', onKeydown);
    bindHeaderCart();
    watchExternalCartChanges();
  }

  window.SideCart = {
    open: open,
    close: close,
    refresh: refresh,
    refreshIfChanged: refreshIfChanged,
    openAndRefresh: openAndRefresh,
    render: render
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
